import axios from 'axios';
import config from '../config/environment';

const API_BASE_URL = config.apiUrl;

export type UserType = 'user' | 'field' | 'referee';

export class UniversalAuthService {
  private config = {
    user: {
      endpoint: '/api/user/profile', // используем профиль для валидации
      tokenKey: 'token',
      dataKey: 'user',
      refreshTokenKey: 'refreshToken'
    },
    field: {
      endpoint: '/api/field/profile',
      tokenKey: 'fieldToken',
      dataKey: 'fieldData',
      refreshTokenKey: 'fieldRefreshToken'
    },
    referee: {
      endpoint: '/api/referee/profile', // используем профиль для валидации
      tokenKey: 'refereeToken', 
      dataKey: 'refereeData',
      refreshTokenKey: 'refereeRefreshToken'
    }
  };

  // Получить данные из localStorage для типа пользователя
  getStoredData(userType: UserType) {
    const { tokenKey, dataKey, refreshTokenKey } = this.config[userType];
    const token = localStorage.getItem(tokenKey);
    const refreshToken = localStorage.getItem(refreshTokenKey);
    const userData = localStorage.getItem(dataKey);

    return {
      token,
      userData: userData ? JSON.parse(userData) : null,
      refreshToken
    };
  }

  // Сохранить данные в localStorage для типа пользователя  
  saveData(userType: UserType, userData: any, token: string, refreshToken?: string) {
    const { tokenKey, dataKey, refreshTokenKey } = this.config[userType];
    localStorage.setItem(tokenKey, token);
    if (refreshToken) {
      localStorage.setItem(refreshTokenKey, refreshToken);
    }
    localStorage.setItem(dataKey, JSON.stringify(userData));
    
    if (refreshToken) {
      localStorage.setItem(refreshTokenKey, refreshToken);
    }
  }

  // Очистить данные из localStorage для типа пользователя
  clearData(userType: UserType) {
    const { tokenKey, dataKey, refreshTokenKey } = this.config[userType];
    localStorage.removeItem(tokenKey);
    localStorage.removeItem(refreshTokenKey);
    localStorage.removeItem(dataKey);
    localStorage.removeItem(refreshTokenKey);
  }

  // Валидация токена для любого типа пользователя
  async validateToken(userType: UserType): Promise<boolean> {
    const { token } = this.getStoredData(userType);
    if (!token) {
      return false;
    }

    try {
      // Проверяем структуру JWT токена
      const parts = token.split('.');
      if (parts.length !== 3) {
        return false;
      }

      // Проверяем срок действия из payload
      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < now) {
        console.log(`❌ Token expired for ${userType}!`);
        return false;
      }

      // Для referee и field - проверяем через API
      if (userType === 'referee' || userType === 'field') {
        try {
          const { endpoint } = this.config[userType];
          const headers = this.getAuthHeaders(userType);
          await axios.get(`${API_BASE_URL}${endpoint}`, { headers });
        } catch (error: any) {
          console.log(`Token validation failed for ${userType}:`, error.response?.status);
          return false;
        }
      }

      // Для user - только проверяем JWT структуру и срок действия
      console.log(`✅ Token valid for ${userType}`);
      return true;
    } catch (error: any) {
      console.log(`Token validation error for ${userType}:`, error);
      return false;
    }
  }

  // Получить заголовки авторизации для типа пользователя
  getAuthHeaders(userType: UserType): Record<string, string> {
    const { token } = this.getStoredData(userType);
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  // Обновить токены для типа пользователя
  updateTokens(userType: UserType, token: string, refreshToken: string) {
    const { tokenKey, refreshTokenKey } = this.config[userType];
    localStorage.setItem(tokenKey, token);
    localStorage.setItem(refreshTokenKey, refreshToken);
  }

  // Извлечение данных из JWT токена
  private decodeJwtToken(token: string): any {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      return JSON.parse(atob(parts[1]));
    } catch (error) {
      console.error('Error decoding JWT token:', error);
      return null;
    }
  }

  // Инициализация авторизации для типа пользователя
  async initializeAuth(userType: UserType): Promise<any | null> {
    try {
      const { token, userData } = this.getStoredData(userType);
      
      if (token && userData) {
        const isValidToken = await this.validateToken(userType);
        
        if (isValidToken) {
          // Извлекаем роль и другие данные из JWT токена для пользователя (резервная проверка)
          if (userType === 'user' && !userData.role) {
            const tokenPayload = this.decodeJwtToken(token);

            if (tokenPayload) {
              // Извлекаем роль только если её нет
              if (tokenPayload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']) {
                userData.role = tokenPayload['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
              }

              // Извлекаем emailVerified из токена
              if (tokenPayload['emailVerified']) {
                userData.emailVerified = tokenPayload['emailVerified'] === 'true';
              }
            }
          }

          return userData;
        } else {
          // Токен истёк - пробуем обновить через refresh token
          console.log(`Token expired for ${userType}, attempting refresh...`);
          const newToken = await this.refreshAccessToken(userType);
          if (newToken) {
            console.log(`✅ Token refreshed successfully for ${userType}`);
            return userData;
          }
          console.log(`❌ Refresh failed for ${userType}, clearing data`);
          this.clearData(userType);
        }
      }
      return null;
    } catch (error) {
      this.clearData(userType);
      return null;
    }
  }

  // Обновление access токена с помощью refresh токена
  async refreshAccessToken(userType: UserType): Promise<string | null> {
    const { token, refreshToken } = this.getStoredData(userType);

    if (!token || !refreshToken) {
      return null;
    }

    try {
      const response = await axios.post(`${API_BASE_URL}/api/auth/refresh-token`, {
        token,
        refreshToken
      });

      const newToken = response.data.token;
      const newRefreshToken = response.data.refreshToken;

      // Обновляем токены в localStorage
      const { tokenKey, refreshTokenKey } = this.config[userType];
      localStorage.setItem(tokenKey, newToken);
      if (newRefreshToken) {
        localStorage.setItem(refreshTokenKey, newRefreshToken);
      }

      console.log(`✅ Token refreshed for ${userType}`);
      return newToken;
    } catch (error) {
      console.error(`Failed to refresh token for ${userType}:`, error);
      return null;
    }
  }
}

export const universalAuthService = new UniversalAuthService();